import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { adminApi } from '../services/api';

// Reports what the server cannot see: which dashboard was opened, and how long the
// person stayed on it. The server logs every data CHANGE on its own (one middleware
// in main.py) — this is the other half, and it mirrors how the mobile app pairs
// page_viewed with page_exit.
//
// A view is reported when it ENDS, carrying its duration, so one row says both
// "they opened it" and "for how long" instead of two rows that must be joined.
//
// ⚠️ WHAT THIS MEASURES, and why it is not wall-clock (Ohad, 2026-07-30):
// the first version timed arrival-to-departure, so a laptop closed overnight with a
// dashboard open reported 37 HOURS on a single screen — 9,311 minutes over 59 views
// for one person, while someone actually working the screen the same day showed a
// median of 36 seconds. Two things stop the clock now: the tab going hidden, and the
// person going idle. A number nobody can defend is worse than no number.

// Route → the dashboard id it belongs to. Longest prefix wins, so /exec/meetings/list
// resolves to meetings_list and not to meetings.
const ROUTE_DASHBOARDS: [string, string][] = [
  ['/exec/meetings/list', 'meetings_list'],
  ['/exec/meetings', 'meetings'],
  ['/exec/patients', 'patients'],
  ['/exec/staff', 'staff'],
  ['/exec/satisfaction', 'satisfaction'],
  ['/exec/wellness', 'wellness'],
  ['/exec/forecast', 'forecast'],
  ['/exec/salary', 'salary'],
  ['/exec/billing', 'billing'],
  ['/exec/ask', 'ask'],
  ['/exec', 'exec'],
  ['/admin', 'admin'],
  ['/dashboard', 'patient_base'],
  ['/', 'patient_base'],
];

const dashboardFor = (path: string): string | undefined =>
  ROUTE_DASHBOARDS.find(([prefix]) => path === prefix || path.startsWith(prefix + '/'))?.[1]
  ?? (path === '/' ? 'patient_base' : undefined);

// A view shorter than this is navigation, not reading — logging it would bury the
// real ones under bounces.
const MIN_SECONDS = 2;

// No click, key or scroll for this long and the clock stops. Reading a dense table
// without touching anything is plausible for a couple of minutes; an hour is not.
const IDLE_MS = 120_000;

export function useActivityLog(enabled: boolean) {
  const location = useLocation();
  // `accrued` is time already banked for this screen; `since` is the clock running
  // right now, or null while it is paused (hidden tab, or nobody there).
  const view = useRef<{ path: string; accrued: number; since: number | null } | null>(null);
  const lastSeen = useRef<number>(Date.now());

  useEffect(() => {
    if (!enabled) return;

    const seconds = () => {
      const v = view.current;
      if (!v) return 0;
      return (v.accrued + (v.since === null ? 0 : Date.now() - v.since)) / 1000;
    };

    const pause = () => {
      const v = view.current;
      if (!v || v.since === null) return;
      v.accrued += Date.now() - v.since;
      v.since = null;
    };

    const resume = () => {
      const v = view.current;
      if (v && v.since === null) v.since = Date.now();
    };

    const close = () => {
      const v = view.current;
      if (!v) return;
      const secs = seconds();
      view.current = null;
      if (secs < MIN_SECONDS) return;
      adminApi.logActivity({
        event: 'page_view',
        dashboard: dashboardFor(v.path),
        path: v.path,
        duration_seconds: Math.round(secs * 100) / 100,
      }).catch(() => {});           // logging must never surface to the user
    };

    // Leaving the previous screen closes its view; entering starts the next one.
    if (view.current && view.current.path !== location.pathname) close();
    if (!view.current) {
      view.current = { path: location.pathname, accrued: 0, since: Date.now() };
    }
    lastSeen.current = Date.now();

    // A hidden tab is not being read. Pausing rather than closing keeps one visit as
    // one row: switch away and come back, and it is still a single view.
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') pause();
      else { lastSeen.current = Date.now(); resume(); }
    };

    // Any interaction means they are still there — and restarts an idle clock.
    const onActivity = () => {
      lastSeen.current = Date.now();
      if (document.visibilityState === 'visible') resume();
    };

    // The one case no browser event announces: they walked away with the tab open.
    const idleCheck = window.setInterval(() => {
      if (Date.now() - lastSeen.current > IDLE_MS) pause();
    }, 15_000);

    // Closing the tab for real. `pagehide` fires where `unload` is unreliable.
    const onLeave = () => close();

    const moves = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onLeave);
    moves.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));

    return () => {
      window.clearInterval(idleCheck);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onLeave);
      moves.forEach((e) => window.removeEventListener(e, onActivity));
    };
  }, [location.pathname, enabled]);
}

/** Called once after a successful sign-in. */
export const logLogin = () =>
  adminApi.logActivity({ event: 'login', path: window.location.pathname }).catch(() => {});
